/**
 * FormFieldRenderer - 表单字段渲染器
 * 
 * 根据 JSON Schema 和 UI Schema 动态渲染单个表单字段。
 * 支持的字段类型：
 * - string: 文本、文本域、邮箱、密码、日期、时间等
 * - number/integer: 数字输入
 * - boolean: 复选框、开关
 * - object: 分组、布局容器、金额字段
 * - array: 子表单/明细表
 * 
 * @example
 * ```tsx
 * <FormFieldRenderer
 *   name="email"
 *   schema={{ type: 'string', format: 'email', title: '邮箱' }}
 *   uiSchema={{ 'ui:placeholder': '请输入邮箱' }}
 *   value={formData.email}
 *   onChange={handleChange}
 *   required={true}
 * />
 * ```
 * 
 * @module forms
 * @see FormRenderer - 表单级别渲染器
 */

// Note: 由于 JSON Schema 的动态特性，部分类型推断较为复杂
// 这些类型断言是安全的，因为运行时会进行正确的类型检查
/* eslint-disable @typescript-eslint/no-explicit-any */

'use client';

import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Bold, Italic, List, ListOrdered, Plus, Quote, Redo2, Star, Strikethrough, Trash2, Undo2 } from 'lucide-react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import DOMPurify from 'dompurify';
import { useTranslation } from '@/hooks/useTranslation';
import type { JSONSchema, UISchema, ValidationError } from './FormRenderer';

// ============================================
// 类型定义
// ============================================

/**
 * FormFieldRenderer 组件属性
 */
export interface FormFieldRendererProps {
  /** 字段名称 */
  name: string;
  /** JSON Schema 定义 */
  schema: JSONSchema;
  /** UI Schema 配置 */
  uiSchema: UISchema;
  /** 字段值 */
  value: unknown;
  /** 值变化回调 */
  onChange: (name: string, value: unknown) => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否必填 */
  required?: boolean;
  /** 验证错误列表 */
  errors?: ValidationError[];
  /** 语言设置 */
  locale?: string;
}

// ============================================
// 主组件
// ============================================

/**
 * 表单字段渲染器主组件
 * 
 * 根据 schema.type 和 uiSchema['ui:widget'] 选择合适的渲染器：
 * - string → Input/Textarea/Select/DatePicker
 * - number/integer → NumberInput
 * - boolean → Checkbox/Switch
 * - object → Group/Container/Currency
 * - array → SubTable
 */
export function FormFieldRenderer({
  name,
  schema,
  uiSchema,
  value,
  onChange,
  disabled = false,
  required = false,
  errors = [],
  locale = 'zh-CN',
}: FormFieldRendererProps) {
  const { t } = useTranslation();
  const tr = t.forms.fieldRenderer;

  // ============================================
  // 解构配置
  // ============================================

  const {
    type,
    title,
    description,
    enum: enumValues,
    format,
  } = schema;

  const {
    'ui:widget': widget,
    'ui:placeholder': placeholder,
    'ui:help': help,
    'ui:disabled': uiDisabled,
    'ui:readonly': readonly,
    'ui:options': options = {},
  } = uiSchema;

  const isDisabled = Boolean(disabled || uiDisabled || readonly);
  const hasError = errors.length > 0;

  // ============================================
  // 类型安全的辅助函数
  // ============================================

  /**
   * 安全获取字符串值
   */
  const getStringValue = (val: unknown): string => {
    if (val === null || val === undefined) return '';
    return String(val);
  };

  /**
   * 安全获取数字值
   */
  const getNumberValue = (val: unknown): number | '' => {
    if (val === null || val === undefined || val === '') return '';
    return Number(val);
  };

  // ============================================
  // 辅助渲染函数
  // ============================================

  /**
   * 渲染字段标签
   */
  const renderLabel = () => (
    <Label htmlFor={name} className="flex items-center gap-1">
      <span>{title || name}</span>
      {required && <span className="text-red-500">*</span>}
    </Label>
  );

  /**
   * 渲染字段描述/帮助文本
   */
  const renderDescription = () => {
    const desc = help || description;
    if (!desc) return null;
    return <p className="text-xs text-gray-500 mt-1">{desc}</p>;
  };

  /**
   * 渲染错误信息
   */
  const renderErrors = () => {
    if (!hasError) return null;
    return (
      <div className="flex items-start gap-1 text-red-600 text-xs mt-1">
        <AlertCircle className="w-3 h-3 mt-0.5" />
        <span>{errors[0].message}</span>
      </div>
    );
  };

  // 根据类型和 widget 选择渲染组件
  const renderField = () => {
    // String 类型
    if (type === 'string') {
      // serialNumber readOnly+disabled：阻止编辑；真值由后端提交时生成
      if (widget === 'serialNumber') {
        return (
          <Input
            id={name}
            type="text"
            value={String(value || '')}
            readOnly
            disabled
            placeholder={(placeholder as string) || tr.serialNumber.placeholder}
            className="bg-gray-50 text-gray-600 font-mono"
          />
        );
      }

      if (widget === 'region') {
        const regionNames = new Intl.DisplayNames([locale], { type: 'region' });
        return (
          <Select
            value={String(value || '')}
            onValueChange={(v) => onChange(name, v)}
            disabled={isDisabled}
          >
            <SelectTrigger className={hasError ? 'border-red-500' : ''}>
              <SelectValue placeholder={(placeholder as string) || tr.region.placeholder} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {REGION_CODES.map((code) => (
                <SelectItem key={code} value={code}>
                  <span className="flex items-center gap-2">
                    <span>{regionFlag(code)}</span>
                    <span>{regionNames.of(code) || code}</span>
                    <span className="text-xs text-gray-400">({code})</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }

      if (widget === 'signature') {
        return (
          <SignaturePad
            value={String(value || '')}
            onChange={(v) => onChange(name, v)}
            disabled={isDisabled}
            hasError={hasError}
            labels={tr.signature}
          />
        );
      }

      if (widget === 'richtext') {
        return (
          <RichTextEditor
            value={String(value || '')}
            onChange={(html) => onChange(name, html)}
            disabled={isDisabled}
            placeholder={(placeholder as string) || tr.richtext.placeholder}
            hasError={hasError}
            labels={tr.richtext}
          />
        );
      }

      // 枚举值 - 下拉选择
      if (enumValues && Array.isArray(enumValues) && enumValues.length > 0) {
        return (
          <Select
            value={String(value || '')}
            onValueChange={(val) => onChange(name, val)}
            disabled={isDisabled}
          >
            <SelectTrigger className={hasError ? 'border-red-500' : ''}>
              <SelectValue placeholder={(placeholder as string) || `请选择${title || name}`} />
            </SelectTrigger>
            <SelectContent>
              {enumValues.map((enumValue) => (
                <SelectItem key={String(enumValue)} value={String(enumValue)}>
                  {String(enumValue)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }

      // 多行文本
      if (widget === 'textarea') {
        return (
          <Textarea
            id={name}
            value={String(value || '')}
            onChange={(e) => onChange(name, e.target.value)}
            disabled={isDisabled}
            placeholder={placeholder as string}
            rows={Number(options.rows) || 4}
            className={hasError ? 'border-red-500' : ''}
          />
        );
      }

      // 邮箱
      if (format === 'email' || widget === 'email') {
        return (
          <Input
            id={name}
            type="email"
            value={String(value || '')}
            onChange={(e) => onChange(name, e.target.value)}
            disabled={isDisabled}
            placeholder={(placeholder as string) || '请输入邮箱'}
            className={hasError ? 'border-red-500' : ''}
          />
        );
      }

      // 密码
      if (widget === 'password') {
        return (
          <Input
            id={name}
            type="password"
            value={String(value || '')}
            onChange={(e) => onChange(name, e.target.value)}
            disabled={isDisabled}
            placeholder={placeholder as string}
            className={hasError ? 'border-red-500' : ''}
          />
        );
      }

      // 日期
      if (format === 'date' || widget === 'date') {
        return (
          <Input
            id={name}
            type="date"
            value={String(value || '')}
            onChange={(e) => onChange(name, e.target.value)}
            disabled={isDisabled}
            className={hasError ? 'border-red-500' : ''}
          />
        );
      }

      // 时间
      if (format === 'time' || widget === 'time') {
        return (
          <Input
            id={name}
            type="time"
            value={String(value || '')}
            onChange={(e) => onChange(name, e.target.value)}
            disabled={isDisabled}
            className={hasError ? 'border-red-500' : ''}
          />
        );
      }

      // 日期时间
      if (format === 'date-time' || widget === 'datetime') {
        return (
          <Input
            id={name}
            type="datetime-local"
            value={String(value || '')}
            onChange={(e) => onChange(name, e.target.value)}
            disabled={isDisabled}
            className={hasError ? 'border-red-500' : ''}
          />
        );
      }

      // 默认文本输入
      return (
        <Input
          id={name}
          type="text"
          value={String(value || '')}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={isDisabled}
          placeholder={placeholder as string}
          className={hasError ? 'border-red-500' : ''}
        />
      );
    }

    if ((type === 'number' || type === 'integer') && widget === 'rating') {
      const maxStars = Number(schema['x-maxStars'] ?? options.maxStars ?? 5);
      const current = Number(value ?? 0);
      return (
        <div className="flex items-center gap-1" role="radiogroup" aria-label={title || name}>
          {Array.from({ length: maxStars }, (_, i) => i + 1).map((star) => {
            const filled = star <= current;
            return (
              <button
                key={star}
                type="button"
                role="radio"
                aria-checked={filled}
                aria-label={`${star}`}
                disabled={isDisabled}
                onClick={() => onChange(name, star === current ? 0 : star)}
                className={`p-0.5 transition-colors ${
                  isDisabled ? 'cursor-not-allowed opacity-60' : 'hover:scale-110 cursor-pointer'
                }`}
              >
                <Star
                  className={`w-6 h-6 ${
                    filled ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                  }`}
                />
              </button>
            );
          })}
          {current > 0 && (
            <span className="ml-2 text-sm text-gray-500">{current} / {maxStars}</span>
          )}
        </div>
      );
    }

    // Number / Integer 类型
    if (type === 'number' || type === 'integer') {
      return (
        <Input
          id={name}
          type="number"
          value={value !== undefined && value !== null ? String(value) : ''}
          onChange={(e) => {
            const val = e.target.value;
            onChange(name, val === '' ? undefined : type === 'integer' ? parseInt(val) : parseFloat(val));
          }}
          disabled={isDisabled}
          placeholder={placeholder as string}
          min={schema.minimum as number | undefined}
          max={schema.maximum as number | undefined}
          step={type === 'integer' ? 1 : 'any'}
          className={hasError ? 'border-red-500' : ''}
        />
      );
    }

    // 金额字段（带货币类型）
    if (type === 'object' && widget === 'currency') {
      const currencyValue = (value as { amount?: number; currency?: string }) || {};
      const defaultCurrency = String(options.currencyType || 'CNY');
      const showCurrencySelect = Boolean(options.showCurrencySelect !== false);
      const allowedCurrencies = (options.allowedCurrencies as string[]) || ['CNY', 'USD', 'EUR', 'JPY', 'GBP', 'HKD'];
      const precision = Number(options.precision ?? 2);

      // 货币符号和名称映射
      const currencyInfo: Record<string, { symbol: string; name: string; flag: string }> = {
        CNY: { symbol: '¥', name: '人民币', flag: '🇨🇳' },
        USD: { symbol: '$', name: '美元', flag: '🇺🇸' },
        EUR: { symbol: '€', name: '欧元', flag: '🇪🇺' },
        JPY: { symbol: '¥', name: '日元', flag: '🇯🇵' },
        GBP: { symbol: '£', name: '英镑', flag: '🇬🇧' },
        HKD: { symbol: 'HK$', name: '港币', flag: '🇭🇰' },
      };

      const currentCurrency = currencyValue.currency || defaultCurrency;
      const currentCurrencyInfo = currencyInfo[currentCurrency] || currencyInfo.CNY;

      return (
        <div className="flex gap-2">
          {/* 货币选择器 */}
          {showCurrencySelect ? (
            <Select
              value={currentCurrency}
              onValueChange={(currency) => {
                onChange(name, { ...currencyValue, currency });
              }}
              disabled={isDisabled}
            >
              <SelectTrigger className={`w-28 ${hasError ? 'border-red-500' : ''}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedCurrencies.map((code: string) => {
                  const info = currencyInfo[code];
                  if (!info) return null;
                  return (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-1.5">
                        <span>{info.flag}</span>
                        <span>{code}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center px-3 bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-600">
              {currentCurrencyInfo.flag} {currentCurrency}
            </div>
          )}
          
          {/* 金额输入 */}
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
              {currentCurrencyInfo.symbol}
            </span>
              <Input
                id={name}
                type="number"
                value={currencyValue.amount !== undefined && currencyValue.amount !== null ? currencyValue.amount : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  const amount = val === '' ? undefined : parseFloat(parseFloat(val).toFixed(precision));
                  onChange(name, { 
                    ...currencyValue, 
                    amount,
                    currency: currentCurrency,
                  });
                }}
                disabled={isDisabled}
                placeholder={(placeholder as string) || '请输入金额'}
                step={precision > 0 ? Math.pow(10, -precision) : 1}
                className={`pl-7 ${hasError ? 'border-red-500' : ''}`}
              />
          </div>
        </div>
      );
    }

    // Boolean 类型
    if (type === 'boolean') {
      // Switch 开关
      if (widget === 'switch') {
        return (
          <div className="flex items-center gap-2">
            <Switch
              id={name}
              checked={!!value}
              onCheckedChange={(checked) => onChange(name, checked)}
              disabled={isDisabled}
            />
            <Label htmlFor={name} className="cursor-pointer">
              {String(options.label || title || name)}
            </Label>
          </div>
        );
      }

      // 默认复选框
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            id={name}
            checked={Boolean(value)}
            onCheckedChange={(checked) => onChange(name, checked)}
            disabled={isDisabled}
          />
          <Label htmlFor={name} className="cursor-pointer">
            {String(options.label || title || name)}
          </Label>
        </div>
      );
    }

    if (type === 'object' && widget === 'address') {
      const addr = (value as { province?: string; city?: string; district?: string; detail?: string }) || {};
      const update = (patch: Partial<typeof addr>) => onChange(name, { ...addr, ...patch });
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Input
              value={addr.province || ''}
              onChange={(e) => update({ province: e.target.value })}
              disabled={isDisabled}
              placeholder={tr.address.provincePlaceholder}
              className={hasError ? 'border-red-500' : ''}
            />
            <Input
              value={addr.city || ''}
              onChange={(e) => update({ city: e.target.value })}
              disabled={isDisabled}
              placeholder={tr.address.cityPlaceholder}
            />
            <Input
              value={addr.district || ''}
              onChange={(e) => update({ district: e.target.value })}
              disabled={isDisabled}
              placeholder={tr.address.districtPlaceholder}
            />
          </div>
          <Textarea
            value={addr.detail || ''}
            onChange={(e) => update({ detail: e.target.value })}
            disabled={isDisabled}
            placeholder={tr.address.detailPlaceholder}
            rows={2}
          />
        </div>
      );
    }

    // Object 类型 - 分组（渲染时不支持折叠，始终展开）
    if (type === 'object' && schema['x-type'] === 'group') {
      const groupProperties = schema.properties || {};
      const groupValue = (value as Record<string, unknown>) || {};
      const groupIcon = schema['x-icon'] as string | undefined;
      
      return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* 分组标题栏 */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100">
            {/* 分组图标 */}
            {groupIcon && (
              <span className="text-base">{groupIcon}</span>
            )}
            <span className="text-sm font-medium text-gray-700 flex-1">
              {title || name}
            </span>
          </div>

          {/* 分组内容 - 始终展开 */}
          <div className="p-4 space-y-4">
            {Object.entries(groupProperties).map(([fieldName, fieldSchema]) => {
              const fs = fieldSchema as JSONSchema;
              const isFieldRequired = (schema.required as string[] | undefined)?.includes(fieldName);
              return (
                  <FormFieldRenderer
                    key={fieldName}
                    name={fieldName}
                    schema={fs}
                    uiSchema={(uiSchema[fieldName] as UISchema) || {}}
                    value={groupValue[fieldName]}
                    onChange={(_, val) => {
                      onChange(name, { ...groupValue, [fieldName]: val });
                    }}
                    disabled={isDisabled}
                    required={isFieldRequired}
                    errors={errors.filter(e => e.field === fieldName)}
                    locale={locale}
                  />
              );
            })}
          </div>
        </div>
      );
    }

    // Object 类型 - 布局容器
    if (type === 'object' && schema['x-type'] === 'container') {
      const containerLayout = schema['x-layout'] as { ratios: number[]; gap?: number } | undefined;
      const ratios = containerLayout?.ratios || [1, 1];
      const gap = containerLayout?.gap || 16;
      const containerProperties = schema.properties || {};
      const containerValue = (value as Record<string, unknown>) || {};
      
      // 按列分组字段
      const columnFields: Array<Array<[string, JSONSchema]>> = ratios.map(() => []);
      Object.entries(containerProperties).forEach(([fieldName, fieldSchema]) => {
        const fs = fieldSchema as JSONSchema;
        const colIndex = (fs['x-column'] as number) || 0;
        if (colIndex < columnFields.length) {
          columnFields[colIndex].push([fieldName, fs]);
        }
      });
      
      // 计算列宽度
      const totalRatio = ratios.reduce((a, b) => a + b, 0);
      const columnWidths = ratios.map(r => (r / totalRatio) * 100);
      
      return (
        <div 
          className="flex"
          style={{ gap: `${gap}px` }}
        >
          {columnFields.map((fields, colIndex) => (
            <div 
              key={colIndex}
              style={{ width: `${columnWidths[colIndex]}%` }}
              className="space-y-4"
            >
              {fields.map(([fieldName, fieldSchema]) => {
                const fs = fieldSchema as JSONSchema;
                const isFieldRequired = (schema.required as string[] | undefined)?.includes(fieldName);
                return (
                    <FormFieldRenderer
                      key={fieldName}
                      name={fieldName}
                      schema={fs}
                      uiSchema={(uiSchema[fieldName] as UISchema) || {}}
                      value={containerValue[fieldName]}
                      onChange={(_, val) => {
                        onChange(name, { ...containerValue, [fieldName]: val });
                      }}
                      disabled={isDisabled}
                      required={isFieldRequired}
                      errors={errors.filter(e => e.field === fieldName)}
                      locale={locale}
                    />
                );
              })}
            </div>
          ))}
        </div>
      );
    }

    if (type === 'array' && widget === 'cascade') {
      const cascadeOptions = (schema['x-cascadeOptions'] as CascadeNode[] | undefined) || [];
      return (
        <CascadeSelect
          options={cascadeOptions}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(v) => onChange(name, v)}
          disabled={isDisabled}
          placeholder={(placeholder as string) || tr.cascade.placeholder}
          emptyText={tr.cascade.empty}
          hasError={hasError}
        />
      );
    }

    // Array 类型 - 子表单/明细表
    if (type === 'array') {
      const itemSchema = schema.items as JSONSchema | undefined;
      const minItems = schema.minItems || 1; // 子表单默认至少1行
      const maxItems = schema.maxItems;

      // 创建空行的辅助函数
      const createEmptyRow = (): Record<string, unknown> => {
        const newRow: Record<string, unknown> = {};
        if (itemSchema?.properties) {
          Object.entries(itemSchema.properties).forEach(([key, fieldSchema]) => {
            const fs = fieldSchema as JSONSchema;
            if (fs.default !== undefined) {
              newRow[key] = fs.default;
            }
          });
        }
        return newRow;
      };

      // 确保至少有 minItems 行数据
      let arrayValue = Array.isArray(value) ? value : [];
      const isObjectArray = itemSchema?.type === 'object' && itemSchema.properties;
      
      // 如果是子表单且行数不足 minItems，补充空行
      if (isObjectArray && arrayValue.length < minItems) {
        const rowsToAdd = minItems - arrayValue.length;
        const newRows = Array.from({ length: rowsToAdd }, () => createEmptyRow());
        arrayValue = [...arrayValue, ...newRows];
        // 同步更新父组件的状态
        setTimeout(() => onChange(name, arrayValue), 0);
      }

      // 添加行
      const handleAddRow = () => {
        if (maxItems && arrayValue.length >= maxItems) return;
        onChange(name, [...arrayValue, createEmptyRow()]);
      };

      // 删除行
      const handleDeleteRow = (index: number) => {
        if (arrayValue.length <= minItems) return;
        const newValue = arrayValue.filter((_, i) => i !== index);
        onChange(name, newValue);
      };

      // 更新行中的字段
      const handleRowFieldChange = (rowIndex: number, fieldName: string, fieldValue: unknown) => {
        const newValue = arrayValue.map((row, i) => {
          if (i === rowIndex) {
            return { ...row, [fieldName]: fieldValue };
          }
          return row;
        });
        onChange(name, newValue);
      };

      // 如果是对象数组（子表单）- 与设计页面保持一致的布局
      if (itemSchema?.type === 'object' && itemSchema.properties) {
        // ✅ 使用 x-column-order 保持与设计时一致的列顺序
        const props = itemSchema.properties;
        const columnOrder = (itemSchema['x-column-order'] as string[]) || Object.keys(props);
        const columns = columnOrder.map(colName => [colName, props[colName]] as [string, JSONSchema]);
        
        return (
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            {/* 子表单主体 - 横向布局 */}
            <div className="flex">
              {/* 序号列 - 固定左侧 */}
              <div className="w-12 flex-shrink-0 flex flex-col sticky left-0 z-10 border-r border-gray-200 bg-white">
                {/* 序号表头 */}
                <div className="h-10 flex items-center justify-center bg-gray-50 border-b border-gray-200">
                  <span className="text-xs text-gray-500">#</span>
                </div>
                {/* 序号数据 */}
                {arrayValue.length === 0 ? (
                  <div className="flex-1" />
                ) : (
                  arrayValue.map((_, rowIndex) => (
                    <div
                      key={rowIndex}
                      className="flex-1 py-3 flex items-center justify-center text-xs text-gray-500 border-b border-gray-100 last:border-b-0"
                    >
                      {rowIndex + 1}
                    </div>
                  ))
                )}
              </div>

              {/* 数据列区域 - 可滚动 */}
              <div className="flex-1 overflow-x-auto">
                <div className="flex min-w-full">
                  {columns.map(([colName, colSchema]) => {
                    const cs = colSchema as JSONSchema;
                    const isRequired = itemSchema.required?.includes(colName);
                    return (
                      <div
                        key={colName}
                        className="flex-1 min-w-[120px] flex-shrink-0 flex flex-col border-r border-gray-200 last:border-r-0"
                      >
                        {/* 列表头 */}
                        <div className="h-10 px-3 flex items-center bg-gray-50 border-b border-gray-200">
                          <span className="text-xs font-medium text-gray-700 truncate">
                            {cs.title || colName}
                            {isRequired && <span className="text-red-500 ml-0.5">*</span>}
                          </span>
                        </div>
                        {/* 列数据 */}
                        {arrayValue.length === 0 ? (
                          <div className="flex-1" />
                        ) : (
                          arrayValue.map((row, rowIndex) => (
                            <div
                              key={rowIndex}
                              className="flex-1 px-3 py-3 bg-white border-b border-gray-100 last:border-b-0"
                            >
                              <SubTableCellRenderer
                                schema={cs}
                                value={row?.[colName]}
                                onChange={(val) => handleRowFieldChange(rowIndex, colName, val)}
                                disabled={isDisabled}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 操作列 - 固定右侧 */}
              <div className="w-14 flex-shrink-0 flex flex-col sticky right-0 z-10 border-l border-gray-200 shadow-[-2px_0_4px_rgba(0,0,0,0.05)] bg-white">
                {/* 操作表头 */}
                <div className="h-10 flex items-center justify-center bg-gray-50 border-b border-gray-200">
                  <span className="text-xs text-gray-500">操作</span>
                </div>
                {/* 操作按钮 */}
                {arrayValue.length === 0 ? (
                  <div className="flex-1" />
                ) : (
                  arrayValue.map((_, rowIndex) => (
                    <div
                      key={rowIndex}
                      className="flex-1 py-3 flex items-center justify-center border-b border-gray-100 last:border-b-0"
                    >
                      <button
                        type="button"
                        onClick={() => handleDeleteRow(rowIndex)}
                        disabled={isDisabled || arrayValue.length <= minItems}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 空数据提示 */}
            {arrayValue.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-400 text-sm border-t border-gray-100">
                暂无数据，点击下方按钮添加
              </div>
            )}

            {/* 底部操作栏 */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddRow}
                disabled={isDisabled || (maxItems !== undefined && arrayValue.length >= maxItems)}
                className="h-7 text-xs"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                新增一项
              </Button>
              {maxItems && (
                <span className="text-xs text-gray-400">
                  {arrayValue.length} / {maxItems}
                </span>
              )}
            </div>
          </div>
        );
      }

      // 简单数组（非对象项）：枚举多选 / 日期区间 / 文件上传 / 字符串列表
      const itemEnum = (itemSchema as any)?.enum as Array<string | number> | undefined;
      const itemFormat = (itemSchema as any)?.format as string | undefined;
      const enumLabels = (schema as any)['x-enum-labels'] as Record<string, string> | undefined;
      const selectedArr: any[] = Array.isArray(value) ? value : [];

      const selectedSet = new Set(selectedArr.map((v) => String(v)));

      // 枚举多选（Checkbox / Multi Select）
      if (itemEnum && itemEnum.length > 0) {
        const isMultiSelect = widget === 'multiSelect' || widget === 'select';
        if (isMultiSelect) {
          // 简化：用原生 select multiple
          return (
            <select
              multiple
              disabled={isDisabled}
              value={Array.from(selectedSet)}
              onChange={(e) => {
                const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                onChange(name, opts);
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              size={Math.min(itemEnum.length, 6)}
            >
              {itemEnum.map((opt) => {
                const ov = String(opt);
                return (
                  <option key={ov} value={ov}>
                    {enumLabels?.[ov] ?? ov}
                  </option>
                );
              })}
            </select>
          );
        }
        // 复选框组
        return (
          <div className="space-y-2">
            {itemEnum.map((opt) => {
              const ov = String(opt);
              const checked = selectedSet.has(ov);
              return (
                <label key={ov} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={checked}
                    disabled={isDisabled}
                    onCheckedChange={(c) => {
                      const next = c
                        ? [...selectedArr, opt]
                        : selectedArr.filter((v) => String(v) !== ov);
                      onChange(name, next);
                    }}
                  />
                  <span>{enumLabels?.[ov] ?? ov}</span>
                </label>
              );
            })}
          </div>
        );
      }

      if (widget === 'dateRange') {
        const start = selectedArr[0] ?? '';
        const end = selectedArr[1] ?? '';
        return (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={start}
              disabled={isDisabled}
              onChange={(e) => onChange(name, [e.target.value, end])}
            />
            <span className="text-gray-400">~</span>
            <Input
              type="date"
              value={end}
              disabled={isDisabled}
              onChange={(e) => onChange(name, [start, e.target.value])}
            />
          </div>
        );
      }

      // 文件 / 图片上传（items.format = 'data-url' / 'binary'，widget = 'image' / 'file'）
      const isUpload =
        widget === 'image' || widget === 'file' ||
        itemFormat === 'data-url' || itemFormat === 'binary';
      if (isUpload) {
        const accept = widget === 'image' ? 'image/*' : undefined;
        const readAsDataUrl = (f: File) => new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(f);
        });
        const handleFile = async (files: FileList | null) => {
          if (!files) return;
          const items = await Promise.all(Array.from(files).map(readAsDataUrl));
          onChange(name, [...selectedArr, ...items]);
        };
        return (
          <div className="space-y-2">
            {selectedArr.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedArr.map((item: string, idx: number) => (
                  <div
                    key={idx}
                    className="relative inline-flex items-center gap-2 px-2 py-1 border border-gray-200 rounded text-xs bg-gray-50"
                  >
                    {widget === 'image' && typeof item === 'string' && item.startsWith('data:image') ? (
                      <img
                        src={item}
                        alt={`${name}-${idx}`}
                        className="h-12 w-12 object-cover rounded"
                      />
                    ) : (
                      <span className="max-w-[180px] truncate">
                        {typeof item === 'string' ? item.slice(0, 40) : `file ${idx + 1}`}
                      </span>
                    )}
                    {!isDisabled && (
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => {
                          const next = selectedArr.filter((_, i) => i !== idx);
                          onChange(name, next);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!isDisabled && (maxItems === undefined || selectedArr.length < maxItems) && (
              <Input
                type="file"
                accept={accept}
                multiple={maxItems === undefined || maxItems > 1}
                onChange={(e) => handleFile(e.target.files)}
              />
            )}
          </div>
        );
      }

      // 兜底：字符串列表（每行一个 input）
      return (
        <div className="space-y-2">
          {selectedArr.map((item: any, idx: number) => (
            <div key={idx} className="flex gap-2">
              <Input
                value={String(item ?? '')}
                disabled={isDisabled}
                onChange={(e) => {
                  const next = [...selectedArr];
                  next[idx] = e.target.value;
                  onChange(name, next);
                }}
              />
              {!isDisabled && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onChange(name, selectedArr.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
          {!isDisabled && (maxItems === undefined || selectedArr.length < maxItems) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onChange(name, [...selectedArr, ''])}
            >
              <Plus className="w-3 h-3 mr-1" />
              添加
            </Button>
          )}
        </div>
      );
    }

    // Object 类型（非 group / container / currency）：递归渲染嵌套字段
    if (type === 'object') {
      const props = (schema.properties || {}) as Record<string, JSONSchema>;
      const keys = Object.keys(props);
      if (keys.length === 0) {
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-sm text-gray-500">无字段</p>
          </div>
        );
      }
      const objectValue: Record<string, any> = (value && typeof value === 'object' && !Array.isArray(value))
        ? (value as Record<string, any>)
        : {};
      return (
        <div className="space-y-3 p-3 border border-gray-200 rounded-lg bg-gray-50/50">
          {keys.map((key) => {
            const childSchema = props[key];
            const childUi = ((uiSchema || {})[key] as UISchema) || {};
            return (
              <FormFieldRenderer
                key={key}
                name={`${name}.${key}`}
                schema={childSchema}
                uiSchema={childUi}
                value={objectValue[key]}
                onChange={(_childName, childVal) => {
                  onChange(name, { ...objectValue, [key]: childVal });
                }}
                required={schema.required?.includes(key) ?? false}
                disabled={isDisabled}
                errors={errors}
              />
            );
          })}
        </div>
      );
    }

    // 未知类型
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-sm text-red-600">不支持的字段类型: {type}</p>
      </div>
    );
  };

  // 判断是否需要显示外层标签（分组、布局容器、数组等有自己的标题/结构）
  const isGroup = type === 'object' && schema['x-type'] === 'group';
  const isContainer = type === 'object' && schema['x-type'] === 'container';
  // array 类型默认让子组件自带标题（subtable / dateRange 等），但 cascade 是一个普通选择控件，需要外层标签
  const isPlainArrayWidget = type === 'array' && widget === 'cascade';
  const shouldShowLabel = type !== 'boolean' && (type !== 'array' || isPlainArrayWidget) && !isGroup && !isContainer;

  return (
    <div className="space-y-2">
      {/* 显示标签（排除自带标题的组件类型） */}
      {shouldShowLabel && renderLabel()}
      
      {/* 字段渲染 */}
      {renderField()}
      
      {/* 描述信息 */}
      {renderDescription()}
      
      {/* 错误信息 */}
      {renderErrors()}
    </div>
  );
}

// ============================================
// 子表单单元格渲染器
// ============================================

interface SubTableCellRendererProps {
  schema: JSONSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

function SubTableCellRenderer({
  schema,
  value,
  onChange,
  disabled = false,
}: SubTableCellRendererProps) {
  const { type, enum: enumValues, enumNames, format } = schema;
  
  // ✅ 从 schema 的 x- 扩展字段中读取 placeholder
  const placeholder = (schema['x-placeholder'] as string) || '请输入';
  
  const inputClass = "w-full h-7 px-2 border border-gray-200 rounded text-xs bg-white focus:outline-none focus:border-blue-400 disabled:bg-gray-50";

  // String 类型
  if (type === 'string') {
    // 枚举值 - 下拉选择
    if (enumValues && enumValues.length > 0) {
      // ✅ 使用 enumNames 作为显示标签，enumValues 作为值
      const labels = (enumNames as string[]) || (enumValues as string[]);
      return (
        <Select
          value={(value as string) || ''}
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {(enumValues as string[]).map((enumValue, index) => (
              <SelectItem key={enumValue} value={String(enumValue)} className="text-xs">
                {labels[index] || enumValue}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // 日期
    if (format === 'date') {
      return (
        <input
          type="date"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputClass}
        />
      );
    }

    // 时间
    if (format === 'time') {
      return (
        <input
          type="time"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputClass}
        />
      );
    }

    // 日期时间
    if (format === 'date-time') {
      return (
        <input
          type="datetime-local"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputClass}
        />
      );
    }

    // 默认文本输入
    return (
      <input
        type="text"
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className={inputClass}
      />
    );
  }

  // Number / Integer 类型
  if (type === 'number' || type === 'integer') {
    return (
      <input
        type="number"
        value={value !== undefined && value !== null ? String(value) : ''}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val === '' ? undefined : type === 'integer' ? parseInt(val) : parseFloat(val));
        }}
        disabled={disabled}
        placeholder={placeholder}
        min={schema.minimum}
        max={schema.maximum}
        step={type === 'integer' ? 1 : 'any'}
        className={inputClass}
      />
    );
  }

  // Boolean 类型
  if (type === 'boolean') {
    return (
      <div className="flex items-center h-7">
        <Checkbox
          checked={!!value}
          onCheckedChange={(checked) => onChange(checked)}
          disabled={disabled}
        />
      </div>
    );
  }

  // 不支持的类型
  return (
    <span className="text-xs text-gray-400">不支持</span>
  );
}

// 国家/地区代码（ISO 3166-1，使用频率排序）；显示名通过 Intl.DisplayNames 按 locale 解析，国旗 emoji 由代码动态合成
const REGION_CODES = [
  'CN', 'US', 'JP', 'KR', 'GB', 'DE', 'FR', 'IT', 'ES', 'CA',
  'AU', 'NZ', 'SG', 'MY', 'TH', 'VN', 'ID', 'PH', 'IN', 'RU',
  'BR', 'MX', 'AE', 'SA', 'ZA', 'HK', 'TW', 'MO', 'NL', 'CH',
  'SE', 'NO', 'FI', 'DK',
] as const;

function regionFlag(code: string): string {
  // ISO 二字母 → 区域指示符号 (Regional Indicator Symbols)
  return code.toUpperCase().replace(/./g, (c) =>
    String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0))
  );
}

// ============================================
// 级联选择
// ============================================

interface CascadeNode {
  value: string;
  label: string;
  children?: CascadeNode[];
}

interface CascadeSelectProps {
  options: CascadeNode[];
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyText: string;
  hasError?: boolean;
}

function CascadeSelect({ options, value, onChange, disabled, placeholder, emptyText, hasError }: CascadeSelectProps) {
  // 按层级展开当前可选列表
  const levels: CascadeNode[][] = [options];
  for (let i = 0; i < value.length; i++) {
    const picked = levels[i]?.find((n) => n.value === value[i]);
    if (picked?.children?.length) levels.push(picked.children);
    else break;
  }

  if (options.length === 0) {
    return (
      <div className={`px-3 py-2 border rounded-md text-sm text-gray-400 bg-gray-50 ${hasError ? 'border-red-500' : 'border-gray-200'}`}>
        {emptyText}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {levels.map((nodes, depth) => (
        <Select
          key={depth}
          value={value[depth] || ''}
          onValueChange={(v) => {
            const next = [...value.slice(0, depth), v];
            onChange(next);
          }}
          disabled={disabled || nodes.length === 0}
        >
          <SelectTrigger className={`min-w-[120px] ${hasError ? 'border-red-500' : ''}`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {nodes.map((n) => (
              <SelectItem key={n.value} value={n.value}>
                {n.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}
    </div>
  );
}

// ============================================
// 手写签名
// ============================================

interface SignaturePadProps {
  value: string;
  onChange: (dataUrl: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  labels: { clear: string; signed: string; hint: string };
}

function SignaturePad({ value, onChange, disabled, hasError, labels }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasDrawing, setHasDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasDrawing(true);
      };
      img.src = value;
    } else {
      setHasDrawing(false);
    }
  }, [value]);

  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    drawingRef.current = true;
    lastPointRef.current = getPos(e);
    canvasRef.current?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !lastPointRef.current) return;
    const p = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1f2937';
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    setHasDrawing(true);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    canvasRef.current?.releasePointerCapture(e.pointerId);
    const dataUrl = canvasRef.current?.toDataURL('image/png');
    if (dataUrl) onChange(dataUrl);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasDrawing(false);
    onChange('');
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={`w-full border rounded-md bg-white touch-none ${
          hasError ? 'border-red-500' : 'border-gray-200'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-crosshair'}`}
        style={{ maxHeight: 180 }}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClear}
          disabled={disabled || !hasDrawing}
        >
          {labels.clear}
        </Button>
        <span className="text-xs text-gray-400">
          {hasDrawing ? labels.signed : labels.hint}
        </span>
      </div>
    </div>
  );
}


// ============================================
// 富文本编辑器（TipTap）
// ============================================

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  labels: {
    loading: string;
    bold: string;
    italic: string;
    strike: string;
    bulletList: string;
    orderedList: string;
    blockquote: string;
    undo: string;
    redo: string;
  };
}

const RICHTEXT_EXTENSIONS = [StarterKit];
const EMPTY_RICHTEXT_HTML = '<p></p>';
// 白名单仅保留 StarterKit 实际能产生的标签，限制 href 协议；防止恶意 PATCH 直接注入 script/iframe/事件属性
const RICHTEXT_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 's', 'u', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'hr'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

function sanitizeRichText(html: string): string {
  return DOMPurify.sanitize(html, RICHTEXT_SANITIZE_CONFIG);
}

function normalizeRichText(html: string) {
  return html === EMPTY_RICHTEXT_HTML ? '' : html;
}

function RichTextEditor({ value, onChange, disabled, placeholder, hasError, labels }: RichTextEditorProps) {
  // 标记最近一次 onUpdate 派发出去的值，避免回流到 useEffect 重新 setContent 抖动光标
  const lastEmittedRef = useRef<string>(value);

  const editor = useEditor({
    extensions: RICHTEXT_EXTENSIONS,
    content: value,
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none min-h-[120px] px-3 py-2 focus:outline-none ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`,
        'data-placeholder': placeholder || '',
      },
    },
    onUpdate: ({ editor }) => {
      const html = normalizeRichText(sanitizeRichText(editor.getHTML()));
      lastEmittedRef.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const safe = sanitizeRichText(value);
    if (safe === lastEmittedRef.current) return;
    if (safe === normalizeRichText(editor.getHTML())) return;
    editor.commands.setContent(safe, { emitUpdate: false });
    lastEmittedRef.current = safe;
  }, [value, editor]);

  if (!editor) {
    return (
      <div className={`border rounded-md bg-white ${hasError ? 'border-red-500' : 'border-gray-200'}`}>
        <div className="min-h-[120px] px-3 py-2 text-gray-400 text-sm">{labels.loading}</div>
      </div>
    );
  }

  const btnClass = (active: boolean) =>
    `p-1.5 rounded hover:bg-gray-100 ${active ? 'bg-gray-200 text-gray-900' : 'text-gray-600'} disabled:opacity-40 disabled:cursor-not-allowed`;

  return (
    <div className={`border rounded-md bg-white overflow-hidden ${hasError ? 'border-red-500' : 'border-gray-200'}`}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-gray-200 bg-gray-50">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
          className={btnClass(editor.isActive('bold'))}
          aria-label={labels.bold}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
          className={btnClass(editor.isActive('italic'))}
          aria-label={labels.italic}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={disabled}
          className={btnClass(editor.isActive('strike'))}
          aria-label={labels.strike}
        >
          <Strikethrough className="w-4 h-4" />
        </button>
        <span className="w-px h-4 bg-gray-200 mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
          className={btnClass(editor.isActive('bulletList'))}
          aria-label={labels.bulletList}
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
          className={btnClass(editor.isActive('orderedList'))}
          aria-label={labels.orderedList}
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={disabled}
          className={btnClass(editor.isActive('blockquote'))}
          aria-label={labels.blockquote}
        >
          <Quote className="w-4 h-4" />
        </button>
        <span className="w-px h-4 bg-gray-200 mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={disabled || !editor.can().undo()}
          className={btnClass(false)}
          aria-label={labels.undo}
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={disabled || !editor.can().redo()}
          className={btnClass(false)}
          aria-label={labels.redo}
        >
          <Redo2 className="w-4 h-4" />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

